import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useSolPrice } from './SolPriceContext';
import { getAllMarketplaceActivities, enrichActivitiesWithPrice } from '../utils/activityUtils';
import { getSafeSupabaseClient, isSupabaseAvailable } from '../lib/supabase';

const ActivityContext = createContext();

export const useActivity = () => {
  const context = useContext(ActivityContext);
  if (!context) {
    throw new Error('useActivity must be used within an ActivityProvider');
  }
  return context;
};

// Activity types and their configurations
export const ACTIVITY_TYPES = {
  PIECE_CREATED: {
    label: 'Piece Minted',
    icon: 'Plus',
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-500/20',
    description: 'A new piece was minted'
  },
  PIECE_SOLD: {
    label: 'Piece Sold',
    icon: 'ShoppingBag',
    color: 'text-green-400',
    bgColor: 'bg-green-500/20',
    description: 'A piece was sold'
  },
  PIECE_LISTED: {
    label: 'Piece Listed',
    icon: 'Tag',
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/20',
    description: 'A piece was listed for sale'
  },
  PIECE_TRADED: {
    label: 'Trade Executed',
    icon: 'Swap',
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/20',
    description: 'A trade was executed between users'
  },
  COLLECTION_CREATED: {
    label: 'Capsule Minted',
    icon: 'Package',
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-500/20',
    description: 'A new capsule was created'
  },
  OFFER_MADE: {
    label: 'Offer Made',
    icon: 'Gift',
    color: 'text-orange-400',
    bgColor: 'bg-orange-500/20',
    description: 'An offer was made on a piece'
  },
  OFFER_ACCEPTED: {
    label: 'Offer Accepted',
    icon: 'Award',
    color: 'text-green-400',
    bgColor: 'bg-green-500/20',
    description: 'An offer was accepted'
  },
};

export const ActivityProvider = ({ children }) => {
  const [activities, setActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [supabaseError, setSupabaseError] = useState(null);
  
  // Use global SOL price from SolPriceContext
  const { solPrice, calculateUsdPrice } = useSolPrice();

  // Fetch activities from backend
  const fetchActivities = useCallback(async () => {
    // Check if Supabase is available
    if (!isSupabaseAvailable()) {
      console.warn('⚠️ Supabase is not available - activities disabled');
      setSupabaseError(new Error('Supabase client not available'));
      setActivities([]);  // Default to empty activities
      return;
    }

    setIsLoading(true);
    try {
      console.log('📊 ActivityContext: Fetching activities from backend...');
      
      // Fetch all activities from backend (offset 0 for initial load)
      let fetchedActivities = await getAllMarketplaceActivities(100, 0);
      
      // Enrich with real-time SOL price
      if (solPrice) {
        fetchedActivities = await enrichActivitiesWithPrice(fetchedActivities, solPrice);
      }
      
      setActivities(fetchedActivities);
      
      console.log(`✅ ActivityContext: Loaded ${fetchedActivities.length} activities`);
    } catch (error) {
      console.error('❌ ActivityContext: Error fetching activities:', error);
      setSupabaseError(error);
      setActivities([]);  // Default to empty activities on error
      // Don't throw - allow app to continue without activities
    } finally {
      setIsLoading(false);
    }
  }, [solPrice]);

  // Load activities from backend on mount and set up real-time updates
  useEffect(() => {
    // Initial fetch
    fetchActivities();
    
    // Debounce refresh to avoid excessive fetching when many activities come in at once
    let refreshTimeout = null;
    
    const debouncedRefresh = () => {
      if (refreshTimeout) clearTimeout(refreshTimeout);
      refreshTimeout = setTimeout(() => {
        console.log('📊 ActivityContext: Debounced refresh triggered');
        fetchActivities();
      }, 2000); // Wait 2 seconds after last activity before refreshing
    };
    
    // Set up real-time subscriptions for automatic updates (only if Supabase is available)
    let subscription = null;
    try {
      const supabase = getSafeSupabaseClient();
      if (supabase) {
        subscription = supabase
          .channel('context_activity_updates')
          .on('postgres_changes', 
            { 
              event: 'INSERT', 
              schema: 'public', 
              table: 'nft_activity' 
            }, 
            (payload) => {
              console.log('📊 ActivityContext: New NFT activity detected, scheduling debounced refresh...');
              debouncedRefresh();
            }
          )
          .on('postgres_changes', 
            { 
              event: 'INSERT', 
              schema: 'public', 
              table: 'wallet_activity' 
            }, 
            (payload) => {
              console.log('📊 ActivityContext: New wallet activity detected, scheduling debounced refresh...');
              debouncedRefresh();
            }
          )
          .subscribe();
      } else {
        console.warn('⚠️ Skipping realtime subscriptions - Supabase not available');
      }
    } catch (error) {
      console.error('❌ Failed to set up realtime subscriptions:', error);
    }
    
    // Periodic refresh as fallback (less frequent since we have real-time updates)
    const interval = setInterval(() => {
      fetchActivities();
    }, 120000); // Every 2 minutes (reduced from 1 minute)
    
    return () => {
      if (refreshTimeout) clearTimeout(refreshTimeout);
      if (subscription) subscription.unsubscribe();
      clearInterval(interval);
    };
  }, [fetchActivities]);

  // Old mock data code - kept for reference
  /*
  useEffect(() => {
    // Mock data generation is currently disabled for testing
    // Uncomment the code below to re-enable mock data
    
    /*
    // Clear existing data and force mock data for testing
    localStorage.removeItem('outsera_activities');
    
    // Add mock data with real-time SOL price
    const mockActivities = [
        {
          id: 'mock_1',
          type: 'PIECE_CREATED',
          timestamp: Date.now() - 300000, // 5 minutes ago
          timeAgo: '5m ago',
          title: 'Minted "Cyberpunk Jacket"',
          description: 'New piece minted in Streetwear Collection',
          pieceName: 'Cyberpunk Jacket',
          collection: 'Streetwear Collection',
          image: '/images/logo.png',
          price: '2.5 SOL',
          usdPrice: `$${calculateUsdPrice(2.5)}`,
          quantity: '1 piece',
          user: '0x1234...5678', // Creator's wallet
          from: '0x1234...5678', // Creator
          transactionHash: '0xabc123...def456'
        },
        {
          id: 'mock_2',
          type: 'PIECE_SOLD',
          timestamp: Date.now() - 900000, // 15 minutes ago
          timeAgo: '15m ago',
          title: '"Vintage Denim" sold',
          description: 'Sold to 0x9876...5432',
          pieceName: 'Vintage Denim',
          collection: 'Retro Collection',
          image: '/images/logo.png',
          price: '1.8 SOL',
          usdPrice: `$${calculateUsdPrice(1.8)}`,
          quantity: '1 piece',
          user: '0x1111...2222', // Seller's wallet
          from: '0x1111...2222', // Seller
          to: '0x9876...5432', // Buyer
          buyer: '0x9876...5432',
          transactionHash: '0xdef456...ghi789'
        },
        {
          id: 'mock_3',
          type: 'PIECE_LISTED',
          timestamp: Date.now() - 1800000, // 30 minutes ago
          timeAgo: '30m ago',
          title: '"Neon Sneakers" listed for sale',
          description: `Listed for 3.2 SOL ($${calculateUsdPrice(3.2)})`,
          pieceName: 'Neon Sneakers',
          collection: 'Footwear Collection',
          image: '/images/logo.png',
          price: '3.2 SOL',
          usdPrice: `$${calculateUsdPrice(3.2)}`,
          quantity: '1 piece',
          user: '0x3333...4444', // Seller's wallet
          from: '0x3333...4444', // Seller
          transactionHash: '0xghi789...jkl012'
        },
        {
          id: 'mock_4',
          type: 'PIECE_TRADED',
          timestamp: Date.now() - 3600000, // 1 hour ago
          timeAgo: '1h ago',
          title: 'Trade executed',
          description: 'Trade executed between 0x5555...6666 and 0x7777...8888',
          initiator: '0x5555...6666',
          counterparty: '0x7777...8888',
          from: '0x5555...6666', // Trade initiator
          to: '0x7777...8888', // Trade counterparty
          quantity: '3 pieces',
          transactionHash: '0xjkl012...mno345'
        },
        {
          id: 'mock_5',
          type: 'COLLECTION_CREATED',
          timestamp: Date.now() - 7200000, // 2 hours ago
          timeAgo: '2h ago',
          title: 'Capsule "Summer Vibes" created',
          description: 'New capsule with 12 pieces',
          collectionName: 'Summer Vibes',
          pieceCount: 12,
          quantity: '12 pieces',
          image: '/images/logo.png',
          user: '0x9999...0000', // Creator's wallet
          from: '0x9999...0000', // Creator
          capsuleId: 'capsule_summer_vibes_001',
          transactionHash: '0xmno345...pqr678'
        },
        {
          id: 'mock_6',
          type: 'OFFER_MADE',
          timestamp: Date.now() - 10800000, // 3 hours ago
          timeAgo: '3h ago',
          title: 'Offer made on "Gold Chain"',
          description: `Offered 4.5 SOL ($${calculateUsdPrice(4.5)})`,
          pieceName: 'Gold Chain',
          collection: 'Accessories Collection',
          image: '/images/logo.png',
          price: '4.5 SOL',
          usdPrice: `$${calculateUsdPrice(4.5)}`,
          quantity: '1 piece',
          user: '0xaaaa...bbbb', // Offer maker's wallet
          from: '0xaaaa...bbbb', // Offer maker
          to: '0xcccc...dddd', // Piece owner
          transactionHash: '0xmno345...pqr678'
        },
        {
          id: 'mock_7',
          type: 'OFFER_ACCEPTED',
          timestamp: Date.now() - 14400000, // 4 hours ago
          timeAgo: '4h ago',
          title: 'Offer accepted for "Diamond Ring"',
          description: `Accepted offer of 6.7 SOL ($${calculateUsdPrice(6.7)})`,
          pieceName: 'Diamond Ring',
          collection: 'Luxury Collection',
          image: '/images/logo.png',
          price: '6.7 SOL',
          usdPrice: `$${calculateUsdPrice(6.7)}`,
          quantity: '1 piece',
          user: '0xcccc...dddd', // Piece owner's wallet
          from: '0xcccc...dddd', // Piece owner (seller)
          to: '0xaaaa...bbbb', // Offer maker (buyer)
          transactionHash: '0xpqr678...stu901'
        },
        {
          id: 'mock_8',
          type: 'PIECE_CREATED',
          timestamp: Date.now() - 18000000, // 5 hours ago
          timeAgo: '5h ago',
          title: 'Minted "Space Helmet"',
          description: 'New piece minted in Sci-Fi Collection',
          pieceName: 'Space Helmet',
          collection: 'Sci-Fi Collection',
          image: '/images/logo.png',
          price: '5.0 SOL',
          usdPrice: `$${calculateUsdPrice(5.0)}`,
          quantity: '1 piece',
          user: '0xeeee...ffff', // Creator's wallet
          from: '0xeeee...ffff', // Creator
          transactionHash: '0xstu901...vwx234'
        },
        {
          id: 'mock_9',
          type: 'PIECE_SOLD',
          timestamp: Date.now() - 21600000, // 6 hours ago
          timeAgo: '6h ago',
          title: '"Cyber Gloves" sold',
          description: 'Sold to 0xgggg...hhhh',
          pieceName: 'Cyber Gloves',
          collection: 'Tech Collection',
          image: '/images/logo.png',
          price: '2.1 SOL',
          usdPrice: `$${calculateUsdPrice(2.1)}`,
          quantity: '1 piece',
          user: '0xiiii...jjjj',
          from: '0xiiii...jjjj',
          to: '0xgggg...hhhh',
          buyer: '0xgggg...hhhh',
          transactionHash: '0xvwx234...yza567'
        },
        {
          id: 'mock_10',
          type: 'COLLECTION_CREATED',
          timestamp: Date.now() - 25200000, // 7 hours ago
          timeAgo: '7h ago',
          title: 'Capsule "Winter Collection" created',
          description: 'New capsule with 8 pieces',
          collectionName: 'Winter Collection',
          pieceCount: 8,
          quantity: '8 pieces',
          image: '/images/logo.png',
          user: '0xkkkk...llll',
          from: '0xkkkk...llll',
          capsuleId: 'capsule_winter_collection_001',
          transactionHash: '0xyza567...bcd890'
        },
        {
          id: 'mock_11',
          type: 'PIECE_LISTED',
          timestamp: Date.now() - 28800000, // 8 hours ago
          timeAgo: '8h ago',
          title: '"Racing Suit" listed for sale',
          description: `Listed for 4.3 SOL ($${calculateUsdPrice(4.3)})`,
          pieceName: 'Racing Suit',
          collection: 'Sports Collection',
          image: '/images/logo.png',
          price: '4.3 SOL',
          usdPrice: `$${calculateUsdPrice(4.3)}`,
          quantity: '1 piece',
          user: '0xmmmm...nnnn',
          from: '0xmmmm...nnnn',
          transactionHash: '0xbcd890...efg123'
        },
        {
          id: 'mock_12',
          type: 'OFFER_MADE',
          timestamp: Date.now() - 32400000, // 9 hours ago
          timeAgo: '9h ago',
          title: 'Offer made on "Platinum Watch"',
          description: `Offered 7.2 SOL ($${calculateUsdPrice(7.2)}) for 7 days`,
          pieceName: 'Platinum Watch',
          collection: 'Luxury Collection',
          image: '/images/logo.png',
          price: '7.2 SOL',
          usdPrice: `$${calculateUsdPrice(7.2)}`,
          quantity: '1 piece',
          user: '0xoooo...pppp',
          from: '0xoooo...pppp',
          to: '0xqqqq...rrrr',
          transactionHash: '0xefg123...hij456'
        },
        {
          id: 'mock_13',
          type: 'PIECE_TRADED',
          timestamp: Date.now() - 36000000, // 10 hours ago
          timeAgo: '10h ago',
          title: 'Trade executed',
          description: 'Trade executed between 0xssss...tttt and 0xuuuu...vvvv',
          initiator: '0xssss...tttt',
          counterparty: '0xuuuu...vvvv',
          from: '0xssss...tttt',
          to: '0xuuuu...vvvv',
          quantity: '2 pieces',
          transactionHash: '0xhij456...klm789'
        },
        {
          id: 'mock_14',
          type: 'PIECE_CREATED',
          timestamp: Date.now() - 39600000, // 11 hours ago
          timeAgo: '11h ago',
          title: 'Minted "Holographic Jacket"',
          description: 'New piece minted in Future Collection',
          pieceName: 'Holographic Jacket',
          collection: 'Future Collection',
          image: '/images/logo.png',
          price: '3.7 SOL',
          usdPrice: `$${calculateUsdPrice(3.7)}`,
          quantity: '1 piece',
          user: '0xwwww...xxxx',
          from: '0xwwww...xxxx',
          transactionHash: '0xklm789...nop012'
        },
        {
          id: 'mock_15',
          type: 'OFFER_ACCEPTED',
          timestamp: Date.now() - 43200000, // 12 hours ago
          timeAgo: '12h ago',
          title: 'Offer accepted for "Crystal Necklace"',
          description: `Accepted offer of 5.8 SOL ($${calculateUsdPrice(5.8)})`,
          pieceName: 'Crystal Necklace',
          collection: 'Jewelry Collection',
          image: '/images/logo.png',
          price: '5.8 SOL',
          usdPrice: `$${calculateUsdPrice(5.8)}`,
          quantity: '1 piece',
          user: '0xyyyy...zzzz',
          from: '0xyyyy...zzzz',
          to: '0xaaaa...bbbb',
          transactionHash: '0xnop012...qrs345'
        }
      ];
      setActivities(mockActivities);
    *_/
  }, [calculateUsdPrice]);
  */

  // No longer saving to localStorage - all data comes from backend
  // useEffect(() => {
  //   localStorage.setItem('outsera_activities', JSON.stringify(activities));
  // }, [activities]);

  // Generate unique activity ID
  const generateActivityId = () => {
    return `activity_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };


  // Format time ago
  const formatTimeAgo = (timestamp) => {
    const now = Date.now();
    const diff = now - timestamp;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (seconds < 60) return `${seconds}s ago`;
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  // Add a new activity (for local optimistic updates)
  const addActivity = useCallback((activityData) => {
    const activity = {
      id: generateActivityId(),
      timestamp: Date.now(),
      timeAgo: formatTimeAgo(Date.now()),
      user: localStorage.getItem('walletAddress') || 'anonymous',
      ...activityData
    };

    // Add to local state for immediate UI update
    setActivities(prev => [activity, ...prev]);
    
    // Backend will handle persistence via webhooks and edge functions
    // Refresh from backend after a short delay to get the actual record
    setTimeout(() => {
      fetchActivities();
    }, 2000);

    return activity;
  }, [fetchActivities]);

  // Log piece creation
  const logPieceCreated = useCallback((pieceData) => {
    return addActivity({
      type: 'PIECE_CREATED',
      title: `Minted "${pieceData.name}"`,
      description: `New piece minted on Solana blockchain`,
      pieceId: pieceData.id,
      pieceName: pieceData.name,
      collection: pieceData.collection,
      image: pieceData.image,
      price: pieceData.price,
      usdPrice: pieceData.usdPrice,
      rarity: pieceData.rarity,
      transactionHash: pieceData.transactionHash
    });
  }, [addActivity]);

  // Log piece sale
  const logPieceSold = useCallback((pieceData, buyerData) => {
    return addActivity({
      type: 'PIECE_SOLD',
      title: `"${pieceData.name}" sold`,
      description: `Sold to ${buyerData.buyer || 'Unknown Buyer'}`,
      pieceId: pieceData.id,
      pieceName: pieceData.name,
      collection: pieceData.collection,
      image: pieceData.image,
      price: pieceData.price,
      usdPrice: pieceData.usdPrice,
      buyer: buyerData.buyer,
      seller: buyerData.seller,
      transactionHash: buyerData.transactionHash
    });
  }, [addActivity]);

  // Log piece listing
  const logPieceListed = useCallback((pieceData) => {
    return addActivity({
      type: 'PIECE_LISTED',
      title: `"${pieceData.name}" listed for sale`,
      description: `Listed for ${pieceData.price} SOL ($${pieceData.usdPrice})`,
      pieceId: pieceData.id,
      pieceName: pieceData.name,
      collection: pieceData.collection,
      image: pieceData.image,
      price: pieceData.price,
      usdPrice: pieceData.usdPrice,
      transactionHash: pieceData.transactionHash
    });
  }, [addActivity]);

  // Log piece trade
  const logPieceTraded = useCallback((tradeData) => {
    return addActivity({
      type: 'PIECE_TRADED',
      title: `Trade executed`,
      description: `Trade executed between ${tradeData.initiator?.slice(0, 8)}... and ${tradeData.counterparty?.slice(0, 8)}...`,
      tradeId: tradeData.id,
      initiatorPieces: tradeData.initiatorPieces,
      counterpartyPieces: tradeData.counterpartyPieces,
      initiator: tradeData.initiator,
      counterparty: tradeData.counterparty,
      transactionHash: tradeData.transactionHash
    });
  }, [addActivity]);

  // Log sale fee collection
  const logSaleFeeCollected = useCallback((feeData) => {
    return addActivity({
      type: 'SALE_FEE_COLLECTED',
      title: `Sale fee collected`,
      description: `2.5% fee collected from "${feeData.pieceName}" sale (${feeData.feeAmount} SOL)`,
      pieceId: feeData.pieceId,
      pieceName: feeData.pieceName,
      feeAmount: feeData.feeAmount,
      piecePrice: feeData.piecePrice,
      buyer: feeData.buyerWallet,
      seller: feeData.sellerWallet,
      transactionHash: feeData.transactionHash,
      revenueSource: 'piece_sale'
    });
  }, [addActivity]);


  // Log capsule creation
  const logCollectionCreated = useCallback((collectionData) => {
    return addActivity({
      type: 'COLLECTION_CREATED',
      title: `Capsule "${collectionData.name}" created`,
      description: `New capsule with ${collectionData.pieceCount || 0} pieces`,
      collectionId: collectionData.id,
      collectionName: collectionData.name,
      pieceCount: collectionData.pieceCount,
      image: collectionData.image
    });
  }, [addActivity]);

  // Log offer made
  const logOfferMade = useCallback((offerData) => {
    return addActivity({
      type: 'OFFER_MADE',
      title: `Offer made on "${offerData.pieceName}"`,
      description: `Offered ${offerData.price} SOL ($${offerData.usdPrice})`,
      pieceId: offerData.pieceId,
      pieceName: offerData.pieceName,
      collection: offerData.collection,
      image: offerData.image,
      price: offerData.price,
      usdPrice: offerData.usdPrice,
      offerId: offerData.id,
      transactionHash: offerData.transactionHash
    });
  }, [addActivity]);

  // Log offer accepted
  const logOfferAccepted = useCallback((offerData) => {
    return addActivity({
      type: 'OFFER_ACCEPTED',
      title: `Offer accepted for "${offerData.pieceName}"`,
      description: `Accepted offer of ${offerData.price} SOL ($${offerData.usdPrice})`,
      pieceId: offerData.pieceId,
      pieceName: offerData.pieceName,
      collection: offerData.collection,
      image: offerData.image,
      price: offerData.price,
      usdPrice: offerData.usdPrice,
      offerId: offerData.id,
      transactionHash: offerData.transactionHash
    });
  }, [addActivity]);



  // Get activities with filters
  const getActivities = useCallback((filters = {}) => {
    let filtered = [...activities];

    // Filter by type(s)
    if (filters.types && filters.types.length > 0 && !filters.types.includes('all')) {
      filtered = filtered.filter(activity => filters.types.includes(activity.type));
    } else if (filters.type && filters.type !== 'all') {
      // Backward compatibility for single type filter
      filtered = filtered.filter(activity => activity.type === filters.type);
    }

    // Filter by time range
    if (filters.timeRange && filters.timeRange !== 'all') {
      const now = Date.now();
      const timeRanges = {
        '1h': 60 * 60 * 1000,
        '24h': 24 * 60 * 60 * 1000,
        '7d': 7 * 24 * 60 * 60 * 1000,
        '30d': 30 * 24 * 60 * 60 * 1000
      };
      const timeLimit = timeRanges[filters.timeRange];
      if (timeLimit) {
        filtered = filtered.filter(activity => now - activity.timestamp <= timeLimit);
      }
    }

    // Filter by user
    if (filters.user) {
      filtered = filtered.filter(activity => activity.user === filters.user);
    }

    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(activity => 
        activity.title?.toLowerCase().includes(searchTerm) ||
        activity.description?.toLowerCase().includes(searchTerm) ||
        activity.pieceName?.toLowerCase().includes(searchTerm) ||
        activity.collection?.toLowerCase().includes(searchTerm)
      );
    }

    // Sort activities
    filtered.sort((a, b) => b.timestamp - a.timestamp);

    return filtered;
  }, [activities]);

  // Clear all activities (local only - doesn't affect database)
  const clearActivities = useCallback(() => {
    setActivities([]);
    // Refetch from backend
    fetchActivities();
  }, [fetchActivities]);

  // Export activities
  const exportActivities = useCallback((format = 'json') => {
    if (format === 'csv') {
      const csvContent = [
        ['Type', 'Title', 'Description', 'Piece Name', 'Collection', 'Price (SOL)', 'Price (USD)', 'Time', 'Transaction Hash'],
        ...activities.map(activity => [
          activity.type,
          activity.title || '',
          activity.description || '',
          activity.pieceName || '',
          activity.collection || '',
          activity.price || '',
          activity.usdPrice || '',
          activity.timeAgo || '',
          activity.transactionHash || ''
        ])
      ].map(row => row.join(',')).join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `outsera-activity-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } else {
      const dataStr = JSON.stringify(activities, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `outsera-activity-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
    }
  }, [activities]);

  const value = {
    activities,
    isLoading,
    supabaseError,  // Expose error state
    solPrice,
    fetchActivities,
    addActivity,
    logPieceCreated,
    logPieceSold,
    logPieceListed,
    logPieceTraded,
    logSaleFeeCollected,
    logCollectionCreated,
    logOfferMade,
    logOfferAccepted,
    getActivities,
    clearActivities,
    exportActivities,
    formatTimeAgo
  };

  return (
    <ActivityContext.Provider value={value}>
      {children}
    </ActivityContext.Provider>
  );
};

export default ActivityContext;
